import { useEffect, useState } from 'react';
import { Plus, RefreshCw, Loader2, Search, LogOut, Star, Clock, Building2, User, Car } from 'lucide-react';
import { useSupplierVisits, useSuppliers } from '../hooks';
import { useCurrentRoleSubjectPermission } from '../hooks/useCurrentRoleSubjectPermission';
import { VisitForm } from '../components';
import { useI18n } from '@/shared/i18n';
import { useToast } from '@/app/providers';
import type { RegisterVisitDTO, SupplierVisit } from '@yawi/types';

// Rating Modal Component
const RatingModal = ({
  visit,
  onSubmit,
  onCancel,
  loading
}: {
  visit: SupplierVisit;
  onSubmit: (rating: number, feedback?: string) => void;
  onCancel: () => void;
  loading: boolean;
}) => {
  const { t } = useI18n();
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [hoveredRating, setHoveredRating] = useState(0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('suppliers.rate')}</h3>
        <p className="text-sm text-slate-500 mb-4">{visit.supplierName}</p>

        {/* Stars */}
        <div className="flex justify-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              className="p-1 transition-transform hover:scale-110"
            >
              <Star
                className={`w-8 h-8 ${
                  star <= (hoveredRating || rating)
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-slate-300'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Feedback */}
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder={t('common.notes')}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none mb-4"
          rows={3}
        />

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onSubmit(rating, feedback || undefined)}
            disabled={loading || rating === 0}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Active Visit Card Component
const ActiveVisitCard = ({
  visit,
  onCheckout,
  loading
}: {
  visit: SupplierVisit;
  onCheckout?: () => void;
  loading?: boolean;
}) => {
  const { t } = useI18n();

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Display authorizedByUserName (system user) or authorizedByName (free text)
  const authorizedByDisplay = visit.authorizedByUserName || visit.authorizedByName;

  return (
    <div className="bg-white border border-green-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-green-600" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-800">{visit.supplierName}</h3>
            <p className="text-sm text-slate-600 line-clamp-1">{visit.purpose}</p>

            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime(visit.entryTime)}
              </span>
              {authorizedByDisplay && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {authorizedByDisplay}
                </span>
              )}
              {visit.vehiclePlate && (
                <span className="flex items-center gap-1 font-mono">
                  <Car className="w-3 h-3" />
                  {visit.vehiclePlate}
                </span>
              )}
            </div>
          </div>
        </div>

        {onCheckout && (
          <button
            onClick={onCheckout}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogOut className="w-4 h-4" />
            )}
            {t('suppliers.checkOut')}
          </button>
        )}
      </div>
    </div>
  );
};

// Completed Visit Card Component
const CompletedVisitCard = ({
  visit,
  onRate
}: {
  visit: SupplierVisit;
  onRate?: () => void;
}) => {
  const { t } = useI18n();

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-3 h-3 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`}
      />
    ));
  };

  // Display authorizedByUserName (system user) or authorizedByName (free text)
  const authorizedByDisplay = visit.authorizedByUserName || visit.authorizedByName;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-slate-500" />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-slate-800">{visit.supplierName}</h3>
            <p className="text-sm text-slate-500 line-clamp-1">{visit.purpose}</p>

            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDateTime(visit.entryTime)}
                {visit.exitTime && ` → ${formatDateTime(visit.exitTime)}`}
              </span>
              {authorizedByDisplay && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {authorizedByDisplay}
                </span>
              )}
              {visit.vehiclePlate && (
                <span className="flex items-center gap-1 font-mono">
                  <Car className="w-3 h-3" />
                  {visit.vehiclePlate}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {visit.rating ? (
            <div className="flex items-center gap-1">
              {renderStars(visit.rating)}
            </div>
          ) : onRate ? (
            <button
              onClick={onRate}
              className="flex items-center gap-1 px-3 py-1 text-sm text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
            >
              <Star className="w-4 h-4" />
              {t('suppliers.rate')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const SupplierVisitsPage = () => {
  const { t } = useI18n();
  const toast = useToast();
  const {
    canCreate: canCreateVisit,
    canUpdate: canUpdateVisit,
  } = useCurrentRoleSubjectPermission('SupplierVisit');

  const { suppliers } = useSuppliers({ limit: 100, activeOnly: true });
  const {
    visits,
    pagination,
    loading,
    error,
    goToPage,
    searchVisits,
    registerVisit,
    checkoutVisit,
    rateVisit,
    refresh,
  } = useSupplierVisits();

  const [showVisitForm, setShowVisitForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState<unknown>(null);
  const [checkoutLoadingId, setCheckoutLoadingId] = useState<string | null>(null);
  const [ratingVisit, setRatingVisit] = useState<SupplierVisit | null>(null);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Debounce: el texto viaja al servidor, no se filtra la página en memoria.
  useEffect(() => {
    const timer = setTimeout(() => searchVisits(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm, searchVisits]);

  const handleRegisterVisit = async (supplierId: string, data: RegisterVisitDTO) => {
    if (!canCreateVisit) return;
    setFormLoading(true);
    setSubmissionError(null);
    try {
      await registerVisit(supplierId, data);
      setShowVisitForm(false);
      toast.success(t('suppliers.visitRegistered'));
    } catch (err) {
      setSubmissionError(err);
      const errObj = err as Record<string, unknown>;
      if (!Array.isArray(errObj?.message)) {
        toast.error(err instanceof Error ? err.message : t('common.error'));
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleCheckout = async (visit: SupplierVisit) => {
    if (!canUpdateVisit) return;
    setCheckoutLoadingId(visit.id);
    try {
      await checkoutVisit(visit.supplierId, visit.id);
      toast.success(t('suppliers.checkoutSuccess'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setCheckoutLoadingId(null);
    }
  };

  const handleRate = async (rating: number, feedback?: string) => {
    if (!canUpdateVisit) return;
    if (!ratingVisit) return;
    setRatingLoading(true);
    try {
      await rateVisit(ratingVisit.supplierId, ratingVisit.id, { rating, feedback });
      toast.success(t('suppliers.ratingSuccess'));
      setRatingVisit(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setRatingLoading(false);
    }
  };

  // Separar activas/completas es sólo presentación: `visits` ya viene filtrada
  // y paginada por el servidor, así que no se vuelve a filtrar aquí.
  const activeVisits = visits.filter(v => !v.exitTime);
  const completedVisits = visits.filter(v => !!v.exitTime);

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">{t('suppliers.visits')}</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="p-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canCreateVisit && (
            <button
              onClick={() => setShowVisitForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('suppliers.checkIn')}
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Active Visits */}
      {activeVisits.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            {t('suppliers.activeVisits')}
            <span className="text-sm font-normal text-slate-500">({activeVisits.length})</span>
          </h2>
          <div className="space-y-3">
            {activeVisits.map((visit) => (
              <ActiveVisitCard
                key={visit.id}
                visit={visit}
                onCheckout={canUpdateVisit ? () => handleCheckout(visit) : undefined}
                loading={checkoutLoadingId === visit.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Completed Visits */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-800">
            {t('suppliers.completedVisits')}
          </h2>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('suppliers.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        )}

        {/* List */}
        {!loading && (
          <div className="space-y-3">
            {completedVisits.map((visit) => (
              <CompletedVisitCard
                key={visit.id}
                visit={visit}
                onRate={canUpdateVisit && !visit.rating ? () => setRatingVisit(visit) : undefined}
              />
            ))}

            {completedVisits.length === 0 && activeVisits.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>{t('suppliers.noVisits')}</p>
              </div>
            )}

            {completedVisits.length === 0 && activeVisits.length > 0 && (
              <div className="text-center py-8 text-slate-500">
                <p>{t('suppliers.noVisits')}</p>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => goToPage(pagination.page - 1)}
              disabled={!pagination.hasPreviousPage}
              className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              {t('common.previous')}
            </button>
            <span className="px-4 py-1 text-sm text-slate-600">
              {pagination.page} / {pagination.totalPages}
            </span>
            <button
              onClick={() => goToPage(pagination.page + 1)}
              disabled={!pagination.hasNextPage}
              className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      {/* Visit Form Modal */}
      {showVisitForm && canCreateVisit && (
        <VisitForm
          suppliers={suppliers}
          onSubmit={handleRegisterVisit}
          onCancel={() => {
            setShowVisitForm(false);
            setSubmissionError(null);
          }}
          loading={formLoading}
          submissionError={submissionError}
        />
      )}

      {/* Rating Modal */}
      {ratingVisit && canUpdateVisit && (
        <RatingModal
          visit={ratingVisit}
          onSubmit={handleRate}
          onCancel={() => setRatingVisit(null)}
          loading={ratingLoading}
        />
      )}
    </div>
  );
};
